import React, { Fragment, useState, useEffect } from "react";
import {
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";
import { Btn, H4, P } from "../AbstractElements";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { storeDispatch } from "../store";
import { login } from "../store/auth";
import { authService } from "../services/authService";

/**
 * VerifyOtp - 3-step OTP verification for unverified users
 *
 * Step 1: OTP sent automatically on mount → user sees code input
 * Step 2: User enters OTP code and verifies
 * Step 3: User sets a new password
 *
 * Uses the same OTP controller as the mobile app:
 *   POST /otp/send-code
 *   POST /otp/verify-code
 *   POST /otp/changePassword
 */
const VerifyOtp = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get user data passed from Signin via router state
  const userData = location.state?.userData;
  const userEmail = userData?.email || "";

  // Steps: "send" → "verify" → "password"
  const [step, setStep] = useState("send");
  const [otpId, setOtpId] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [togglePassword, setTogglePassword] = useState(false);
  const [toggleConfirm, setToggleConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Redirect to login if no user data
  useEffect(() => {
    if (!userData || !userEmail) {
      navigate(`${process.env.PUBLIC_URL}/login`, { replace: true });
    }
  }, [userData, userEmail, navigate]);

  // Auto-send OTP on mount
  useEffect(() => {
    if (userEmail && step === "send") {
      sendOtp();
    }
  }, [userEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Step 1: Send OTP code to user's email
  const sendOtp = async () => {
    setIsLoading(true);
    try {
      const response = await authService.sendOtp({ email: userEmail, reason: "VALIDATE_EMAIL" });

      if (response?.otpId) {
        setOtpId(response.otpId);
        setStep("verify");
        toast.success(`Verification code sent to ${userEmail}`);
        setResendCooldown(60);
      } else {
        toast.error("Failed to send verification code");
      }
    } catch (error) {
      console.error("Send OTP failed:", error);
      const message = error.response?.data?.message || "Failed to send verification code";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Verify the OTP code
  const verifyOtp = async (e) => {
    e.preventDefault();

    if (!code.trim()) {
      toast.error("Please enter the verification code");
      return;
    }

    setIsLoading(true);
    try {
      const response = await authService.verifyOtp({ email: userEmail, code: code.trim(), otpId, reason: "VALIDATE_EMAIL" });

      if (response?.otpId) {
        setOtpId(response.otpId);
        setStep("password");
        toast.success("Code verified successfully. Please set your new password.");
      } else {
        toast.error("Invalid verification code");
      }
    } catch (error) {
      console.error("Verify OTP failed:", error);
      const message = error.response?.data?.message || "Invalid verification code";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Step 3: Change password
  const changePassword = async (e) => {
    e.preventDefault();

    if (!password) {
      toast.error("Please enter a new password");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setIsLoading(true);
    try {
      const response = await authService.resetPassword({ email: userEmail, password: password.trim(), otpId });

      if (response?.token) {
        // changePassword returns a fresh JwtResponse with verified=true
        toast.success("Password set successfully! Redirecting...");

        // Now dispatch login with the fresh response
        storeDispatch(login({ user: response }));

        setTimeout(() => {
          navigate(`${process.env.PUBLIC_URL}/dashboard`, { replace: true });
        }, 500);
      } else {
        toast.error("Failed to set password. Please try again.");
      }
    } catch (error) {
      console.error("Change password failed:", error);
      const message = error.response?.data?.message || "Failed to set password";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Resend OTP
  const handleResend = () => {
    if (resendCooldown > 0) return;
    setCode("");
    sendOtp();
  };

  // Go back to login
  const handleBackToLogin = () => {
    navigate(`${process.env.PUBLIC_URL}/login`, { replace: true });
  };

  if (!userData) return null;

  return (
    <Fragment>
      <Container fluid={true} className="p-0 login-page">
        <Row>
          <Col xs="12">
            <div className="login-card">
              <div className="login-main login-tab">
                {/* Step: Sending OTP */}
                {step === "send" && (
                  <div className="text-center py-4">
                    <H4>Email Verification</H4>
                    <P>Sending verification code to {userEmail}...</P>
                    <div className="mt-3">
                      <span className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Sending...</span>
                      </span>
                    </div>
                  </div>
                )}

                {/* Step: Verify OTP Code */}
                {step === "verify" && (
                  <Form className="theme-form" onSubmit={verifyOtp}>
                    <H4>Verify Your Email</H4>
                    <P>
                      We sent a verification code to <strong>{userEmail}</strong>.
                      Please enter it below.
                    </P>

                    <FormGroup>
                      <Label className="col-form-label" htmlFor="otpCode">
                        Verification Code
                      </Label>
                      <Input
                        id="otpCode"
                        className="form-control text-center"
                        type="text"
                        maxLength="6"
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                        disabled={isLoading}
                        placeholder="Enter code"
                        autoComplete="one-time-code"
                        autoFocus
                        style={{ fontSize: "1.5rem", letterSpacing: "0.5rem" }}
                      />
                    </FormGroup>

                    <div className="form-group mb-0">
                      <Btn
                        attrBtn={{
                          color: "primary",
                          className: "d-block w-100 mt-2",
                          type: "submit",
                          disabled: isLoading || !code.trim(),
                        }}
                      >
                        {isLoading ? "Verifying..." : "Verify Code"}
                      </Btn>

                      <div className="d-flex justify-content-between mt-3">
                        <button
                          type="button"
                          className="btn btn-link p-0"
                          onClick={handleResend}
                          disabled={resendCooldown > 0}
                        >
                          {resendCooldown > 0
                            ? `Resend Code (${resendCooldown}s)`
                            : "Resend Code"}
                        </button>
                        <button
                          type="button"
                          className="btn btn-link p-0"
                          onClick={handleBackToLogin}
                        >
                          Back to Login
                        </button>
                      </div>
                    </div>
                  </Form>
                )}

                {/* Step: Set New Password */}
                {step === "password" && (
                  <Form className="theme-form" onSubmit={changePassword}>
                    <H4>Set New Password</H4>
                    <P>Please set a new password for your account.</P>

                    <FormGroup className="position-relative">
                      <Label className="col-form-label" htmlFor="newPassword">
                        New Password
                      </Label>
                      <div className="position-relative">
                        <Input
                          id="newPassword"
                          className="form-control"
                          type={togglePassword ? "text" : "password"}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          disabled={isLoading}
                          placeholder="Enter new password"
                          autoComplete="new-password"
                          required
                        />
                        <div
                          className="show-hide"
                          onClick={() => setTogglePassword(!togglePassword)}
                        >
                          <span className={togglePassword ? "" : "show"}></span>
                        </div>
                      </div>
                    </FormGroup>

                    <FormGroup className="position-relative">
                      <Label className="col-form-label" htmlFor="confirmPassword">
                        Confirm Password
                      </Label>
                      <div className="position-relative">
                        <Input
                          id="confirmPassword"
                          className="form-control"
                          type={toggleConfirm ? "text" : "password"}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          disabled={isLoading}
                          placeholder="Confirm new password"
                          autoComplete="new-password"
                          required
                        />
                        <div
                          className="show-hide"
                          onClick={() => setToggleConfirm(!toggleConfirm)}
                        >
                          <span className={toggleConfirm ? "" : "show"}></span>
                        </div>
                      </div>
                    </FormGroup>

                    <div className="form-group mb-0">
                      <Btn
                        attrBtn={{
                          color: "primary",
                          className: "d-block w-100 mt-2",
                          type: "submit",
                          disabled: isLoading || !password || !confirmPassword,
                        }}
                      >
                        {isLoading ? "Setting Password..." : "Set Password"}
                      </Btn>
                    </div>
                  </Form>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default VerifyOtp;
